import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import DataTable, { ColumnDef } from "../../../components/DataTable/DataTable";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { usePortal } from "../../../context/PortalsContext";
import { ReactComponent as DragDropIcon } from "../../../assets/drag-drop.svg";
import {
  getAssignment,
  getAllRules,
  reorderRules,
  deleteRules,
} from "../../../services/api/apiClient";
import { useToast } from "../../../context/ToastContext";
import {
  GetAssignmentResult,
  Status,
} from "../../../services/api/types/assignments/assignment";
import { GetPortalAssignmentRuleResult } from "../../../services/api/types/rules/rules";
import { brand, gray } from "../../../theme/themePrimitives";
import { extractErrorMessage } from "../../../utils/errorUtils";

interface AssignmentRulesProps {}

export const AssignmentRules: React.FC<AssignmentRulesProps> = () => {
  const { portalUid, assignmentUid } = useParams<{
    portalUid: string;
    assignmentUid: string;
  }>();
  const navigate = useNavigate();
  const { portals } = usePortal();
  const { state } = useLocation();

  const [assignment, setAssignment] = useState<GetAssignmentResult | null>(
    state?.assignmentTitle
      ? ({ title: state.assignmentTitle } as GetAssignmentResult)
      : null,
  );
  const [rules, setRules] = useState<GetPortalAssignmentRuleResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRows, setSelectedRows] = useState<
    GetPortalAssignmentRuleResult[]
  >([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const portalId = portals.selectedPortal?.portalUid ?? portalUid ?? "";
  const { showToast } = useToast();

  useEffect(() => {
    if (!portalId || !assignmentUid) return;
    getAssignment(portalId, assignmentUid)
      .then((result) => {
        // The single-assignment endpoint's status can reflect the owning
        // (Kernel) portal rather than this portal's own status. Trust the
        // already-correct, portal-scoped status passed from the assignment
        // list page when we have it, and only fall back to this endpoint's
        // status for direct navigation (e.g. a page refresh) where no
        // better signal is available.
        const activeOnThisPortal: boolean =
          state?.assignmentActiveOnThisPortal ??
          result.status !== Status.INACTIVE;
        if (!activeOnThisPortal) {
          showToast(
            "This assignment is inactive on this portal and cannot be managed.",
            "error",
          );
          navigate(`/portal-configuration/${portalUid}/assignment-search`, {
            replace: true,
          });
          return;
        }
        setAssignment(result);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalId, assignmentUid]);

  const fetchRules = useCallback(
    (currentPage = page, currentLimit = limit, currentSearch = search) => {
      if (!portalId || !assignmentUid) return;
      const offset = (currentPage - 1) * currentLimit;
      setRulesLoading(true);
      getAllRules(portalId, assignmentUid, {
        limit: currentLimit,
        offset,
        search: currentSearch,
      })
        .then((result) => {
          setRules(result.rows);
          setTotalCount(result.count);
        })
        .catch(() => {})
        .finally(() => setRulesLoading(false));
    },
    [portalId, assignmentUid, page, limit, search],
  );

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = rules.findIndex((r) => r.ruleUid === active.id);
      const newIndex = rules.findIndex((r) => r.ruleUid === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const priorityCurrent = rules[oldIndex].priority;
      const priorityNew = rules[newIndex].priority;

      const reordered = arrayMove(rules, oldIndex, newIndex).map((r, i) => ({
        ...r,
        priority: i,
      }));
      setRules(reordered);

      try {
        await reorderRules(portalId, assignmentUid!, {
          ruleUid: active.id as string,
          priorityCurrent,
          priorityNew,
        });
        fetchRules();
      } catch (error: any) {
        showToast(
          extractErrorMessage(
            error,
            "Failed to reorder rules. Please try again.",
          ),
          "error",
        );
        fetchRules();
      }
    },
    [rules, portalId, assignmentUid, fetchRules, showToast],
  );

  const handleDeleteConfirm = useCallback(async () => {
    setDeleteLoading(true);
    const selectedUids = selectedRows.map((r) => r.ruleUid);
    try {
      await deleteRules(portalId, assignmentUid!, { ruleUids: selectedUids });
      setSelectedRows([]);
      setDeleteModalOpen(false);
      setPage(1);
      fetchRules(1, limit, search);
    } catch (error: any) {
      showToast(
        extractErrorMessage(
          error,
          "Failed to delete rule(s). Please try again.",
        ),
        "error",
      );
    } finally {
      setDeleteLoading(false);
    }
  }, [selectedRows, portalId, assignmentUid, showToast, fetchRules, limit, search]);

  const columns: ColumnDef<GetPortalAssignmentRuleResult>[] = useMemo(
    () => [
      {
        id: "name",
        label: "Rule name",
        render: (row) => (
          <Typography
            variant="body2"
            sx={{
              color: brand[600],
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() =>
              navigate(
                `/portal-configuration/${portalUid}/assignment-search/${assignmentUid}/rules/${row.ruleUid}`,
                {
                  state: {
                    ruleName: row.name,
                    assignmentTitle: assignment?.title,
                    assignmentActiveOnThisPortal:
                      state?.assignmentActiveOnThisPortal,
                  },
                },
              )
            }
          >
            {row.name}
          </Typography>
        ),
      },
      {
        id: "filtersText",
        label: (
          <Box display="flex" alignItems="center" gap={0.5}>
            Target audience
            <Tooltip
              title="Who this rule applies to"
              placement="top"
              arrow
              slotProps={{
                tooltip: { sx: { bgcolor: "#1F2937", color: "#fff" } },
                arrow: { sx: { color: "#1F2937" } },
              }}
            >
              <HelpOutlineOutlinedIcon
                sx={{ fontSize: 16, color: gray[400] }}
              />
            </Tooltip>
          </Box>
        ),
        render: (row) => (
          <Typography variant="body2">{row.filtersText}</Typography>
        ),
      },
      {
        id: "deadlineNewStarter",
        label: "New starters",
        render: (row) => (
          <Typography variant="body2">
            {row.deadlineNewStarter != null
              ? `${row.deadlineNewStarter} days`
              : "—"}
          </Typography>
        ),
      },
      {
        id: "deadlineExistingUser",
        label: "Existing staff",
        render: (row) => (
          <Typography variant="body2">
            {row.deadlineExistingUser != null
              ? `${row.deadlineExistingUser} days`
              : "—"}
          </Typography>
        ),
      },
      {
        id: "portalGroupName",
        label: "Placement",
        render: (row) => (
          <Box display="flex" alignItems="center" gap="12px">
            {row.portalGroupName == null ? (
              <HomeOutlinedIcon sx={{ fontSize: 20, color: "#475467" }} />
            ) : (
              <ViewModuleOutlinedIcon sx={{ fontSize: 20, color: "#475467" }} />
            )}
            <Typography variant="body2">
              {row.portalGroupName ?? "Homepage"}
            </Typography>
          </Box>
        ),
      },
      {
        id: "creationDate",
        label: "Creation date",
        render: (row) => (
          <Typography variant="body2">{row.createdAt}</Typography>
        ),
      },
      {
        id: "affectedUsersCount",
        label: (
          <Box display="flex" alignItems="center" gap={0.5}>
            Users matched
            <Tooltip
              title="Users matching this rule may also satisfy one or more other rules."
              placement="top"
              arrow
              slotProps={{
                tooltip: { sx: { bgcolor: "#1F2937", color: "#fff" } },
                arrow: { sx: { color: "#1F2937" } },
              }}
            >
              <HelpOutlineOutlinedIcon
                sx={{ fontSize: 16, color: gray[400] }}
              />
            </Tooltip>
          </Box>
        ),
        render: (row) => (
          <Typography variant="body2">
            {row.affectedUsersCount?.toLocaleString() ?? "—"}
          </Typography>
        ),
      },
    ],
    [navigate, portalUid, assignmentUid, assignment?.title],
  );

  const selectedCount = selectedRows.length;

  usePageMetadata(
    {
      title: assignment?.title || "Assignment rules",
      breadcrumbs: [
        {
          label: "Assignment management",
          path: `/portal-configuration/${portalUid}/assignment-search`,
        },
        {
          label: "Assignment search",
          path: `/portal-configuration/${portalUid}/assignment-search`,
        },
        {
          label: "Rules",
        },
      ],
    },
    [portalUid, assignment?.title],
  );

  return (
    <>
      <Paper sx={{ p: 0 }}>
        <Box sx={{ borderBottom: "1px solid #EAECF0", p: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Existing rules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Executed in order from top to bottom
          </Typography>
        </Box>
        <DataTable
          data={rules}
          columns={columns}
          totalCount={totalCount}
          page={page}
          rowsPerPage={limit}
          onPageChange={setPage}
          onRowsPerPageChange={(rows) => {
            setLimit(rows);
            setPage(1);
          }}
          search={search}
          onSearchChange={(val) => {
            setSearch(val);
            setPage(1);
          }}
          searchPlaceholder="Search by name"
          getRowId={(row) => row.ruleUid}
          enableSelection
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          selectedCountTitle="Selected rules"
          isLoading={rulesLoading}
          enableDragAndDrop
          onDragEnd={handleDragEnd}
          dragHandleIcon={<DragDropIcon />}
          disablePaper
          actions={
            <Box display="flex" alignItems="center" gap={1}>
              {selectedCount > 0 && (
                <>
                  <Tooltip title="Remove">
                    <IconButton
                      onClick={() => setDeleteModalOpen(true)}
                      sx={{
                        border: "1px solid #D0D5DD",
                        backgroundColor: "#FFFFFF",
                        borderRadius: "12px",
                        width: "40px",
                        height: "40px",
                        "&:hover": {
                          border: "2px solid #8A9AEF",
                          backgroundColor: "#FFFFFF",
                          color: brand[600],
                        },
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem />
                </>
              )}

              <Button
                sx={{
                  backgroundColor: `${brand[600]} !important`,
                  color: "white",
                  borderRadius: "12px",
                  padding: "8px 16px",
                  "&:hover": { bgcolor: `${brand[700]} !important` },
                }}
                onClick={() => {
                  localStorage.removeItem("ruleFilters");
                  navigate(
                    `/portal-configuration/${portalUid}/assignment-search/${assignmentUid}/rules/new`,
                    {
                      state: {
                        assignmentTitle: assignment?.title,
                        assignmentActiveOnThisPortal:
                          state?.assignmentActiveOnThisPortal,
                      },
                    },
                  );
                }}
              >
                Add new rule
              </Button>
            </Box>
          }
        />
      </Paper>

      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        mt={2}
        sx={{ color: "text.secondary" }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        <Typography variant="body2">
          The system will stop at the first rule that matches a user. Lower
          rules will not be evaluated.
        </Typography>
      </Box>

      {deleteModalOpen && (
        <ConfirmModal
          openConfirmModal={deleteModalOpen}
          setOpenConfirmModal={setDeleteModalOpen}
          confirmButtonClick={handleDeleteConfirm}
          modalTitle="Remove rule"
          modalSubtitle={`Are you sure you want to remove ${selectedCount} selected rule${selectedCount !== 1 ? "s" : ""}?`}
          modalLoading={deleteLoading}
        />
      )}
    </>
  );
};
